import type { ILanguageResources, ITranslationModule } from '../../../interfaces';
import { interpolate } from '../../../global';

class Id {
  private constructor() {}

  public static readonly prefix = 'UI.scripts.quotes.QuoteItemValidationModal.';
  public static readonly itemNumberTitle = `${Id.prefix}itemNumberTitle`;
  public static readonly frameAndDescriptionTitle = `${Id.prefix}frameAndDescriptionTitle`;
  public static readonly versionTitle = `${Id.prefix}versionTitle`;
  public static readonly statusTitle = `${Id.prefix}statusTitle`;
  public static readonly notProcessed = `${Id.prefix}notProcessed`;
  public static readonly updating = `${Id.prefix}updating`;
  public static readonly saved = `${Id.prefix}saved`;
  public static readonly error = `${Id.prefix}error`;
  public static readonly cancelUpdateConfirm = `${Id.prefix}cancelUpdateConfirm`;
  public static readonly cancellingOperations = `${Id.prefix}cancellingOperations`;
  public static readonly pleaseWaitWhileProcessesCleanUp = `${Id.prefix}pleaseWaitWhileProcessesCleanUp`;
  public static readonly explanation = `${Id.prefix}explanation`;
  public static readonly priceAdjustmentComment = `${Id.prefix}priceAdjustmentComment`;
  public static readonly dialogTitle = `${Id.prefix}dialogTitle`;
  public static readonly validateButton = `${Id.prefix}validateButton`;
  public static readonly processing = `${Id.prefix}processing`;
  public static readonly itemNumber = `${Id.prefix}itemNumber`;
  public static readonly closeButton = `${Id.prefix}closeButton`;
  public static readonly priceChangedWarning = `${Id.prefix}priceChangedWarning`;
}

export class LangQuoteItemValidationModal {
  private constructor() {}

  public static itemNumberTitle(): string {
    return interpolate(Id.itemNumberTitle);
  }

  public static frameAndDescriptionTitle(): string {
    return interpolate(Id.frameAndDescriptionTitle);
  }

  public static versionTitle(): string {
    return interpolate(Id.versionTitle);
  }

  public static statusTitle(): string {
    return interpolate(Id.statusTitle);
  }

  public static notProcessed(): string {
    return interpolate(Id.notProcessed);
  }

  public static updating(): string {
    return interpolate(Id.updating);
  }

  public static saved(): string {
    return interpolate(Id.saved);
  }

  public static error(): string {
    return interpolate(Id.error);
  }

  public static cancelUpdateConfirm(): string {
    return interpolate(Id.cancelUpdateConfirm);
  }

  public static cancellingOperations(): string {
    return interpolate(Id.cancellingOperations);
  }

  public static pleaseWaitWhileProcessesCleanUp(): string {
    return interpolate(Id.pleaseWaitWhileProcessesCleanUp);
  }

  public static explanation(params: { version: string; priceAdjComent: string }): string {
    return interpolate(Id.explanation, params);
  }

  public static priceAdjustmentComment(): string {
    return interpolate(Id.priceAdjustmentComment);
  }

  public static dialogTitle(): string {
    return interpolate(Id.dialogTitle);
  }

  public static validateButton(): string {
    return interpolate(Id.validateButton);
  }

  public static processing(): string {
    return interpolate(Id.processing);
  }

  public static itemNumber(params: { number: number }): string {
    return interpolate(Id.itemNumber, params);
  }

  public static closeButton(): string {
    return interpolate(Id.closeButton);
  }

  public static priceChangedWarning(params: { oldPrice: number; newPrice: number }): string {
    return interpolate(Id.priceChangedWarning, params);
  }
}

export class QuoteItemValidationModalModule implements ITranslationModule {
  registerItems(register: ILanguageResources) {
    // Table columns
    register.add(Id.itemNumberTitle, 'Item #', 'Title for item number column');
    register.add(
      Id.frameAndDescriptionTitle,
      "{{glossary 'frame-title'}} and Description",
      'Title for frame and description column'
    );
    register.add(Id.versionTitle, 'Version', 'Title for version column');
    register.add(Id.statusTitle, 'Status', 'Title for status column');
    // Status badges
    register.add(Id.notProcessed, 'Not Processed', 'Badge for not processed');
    register.add(Id.updating, 'Updating', 'Badge for updating');
    register.add(Id.saved, 'Saved', 'Badge for saved');
    register.add(Id.error, 'Error', 'Badge for error');
    // Dialog and actions
    register.add(
      Id.cancelUpdateConfirm,
      'All updates may not have completed. Do you want to cancel update?',
      'Confirmation for cancelling update'
    );
    register.add(Id.cancellingOperations, 'Cancelling Operations', 'Cancelling operations message');
    register.add(Id.pleaseWaitWhileProcessesCleanUp, 'Please wait while the processes clean up', 'Please wait message');
    register.add(
      Id.explanation,
      `## Information

### Current {{glossary 'supplier'}} version: **{{version}}**

The {{glossary 'frame:plural'}} shown need to be validated and re-saved because one of the following things may have occurred
since these items were last saved:

+ The configuration service may have updated
+ {{glossary 'supplier'}} pricing may have been updated
+ {{glossary 'quote'}} properties have been edited which may effect {{glossary 'frame:plural'}} or {{glossary 'quote'}} construction and/or pricing

Please check each {{glossary 'frame'}} carefully after validation to ensure prices and configuration are still
as expected. Prices may vary because of {{glossary 'supplier'}} price updates, but it could also be sign of an unexpected change
in the structure of the {{glossary 'frame'}}.
{{priceAdjComent}}`,
      'Explanation markdown for out-of-date items'
    );
    register.add(
      Id.priceAdjustmentComment,
      `Revalidation may result in changes that affect the price on items with supplier price adjustments 
<webmodule-icon library="fa" name="fas-tags" class="text-base"></webmodule-icon>, if so, these adjustments will be discarded.
You will then need to reactivate the associated support ticket to request a supplier review.`,
      'Comment about price adjustments'
    );
    register.add(Id.dialogTitle, "Validate out-of-date {{glossary 'frame:plural'}}", 'Dialog title');
    register.add(Id.validateButton, 'Validate', 'Validate button');
    register.add(Id.processing, 'Processing', 'Processing label');
    register.add(Id.itemNumber, '#{{number}}', 'Item number label');
    register.add(Id.closeButton, 'Close', 'Close button');
    register.add(
      Id.priceChangedWarning,
      'Price has changed from {{oldPrice}} to {{newPrice}}',
      'Warning message for price change'
    );
  }
}
